import _ from 'lodash';
import { useState } from 'react';
import documentUploadApiUrl from '../api/documentUploadApiUrl';
import { getToken } from '../auth/UserService';

type Upload = {
  progress: number;
  isUploading: boolean;
  error: Error | null;
  url: string | null;
};

export const useUpload = () => {
  const [upload, setUpload] = useState<Upload>({
    progress: 0,
    isUploading: false,
    error: null,
    url: null,
  });

  const uploadFile = async (
    file: File,
    folderId: string,
    referenceId: number
  ) => {
    setUpload({ progress: 0, isUploading: true, error: null, url: null });
    try {
      const { url } = await uploadSplitFile(
        file,
        folderId,
        referenceId,
        progress =>
          setUpload({ progress, isUploading: true, error: null, url: null })
      );
      setUpload({ progress: 100, isUploading: false, error: null, url });
    } catch (error) {
      setUpload({
        progress: 0,
        isUploading: false,
        error: error as Error,
        url: null,
      });
    }
  };

  return { upload, uploadFile };
};
const apiRoot = documentUploadApiUrl;
function initUpload(
  file: File,
  folderId: string,
  headers?: any
): Promise<{
  saveUrl: string;
  uploadUrl: string;
}> {
  return fetch(apiRoot + '/signature/initial-generate', {
    method: 'POST',
    mode: 'cors',
    body: JSON.stringify({
      fileName: file.name,
      folderId,
    }),
    headers,
  }).then(response => response.json());
}
/**
 * Measure network speed by uploading a file of size [headerSize]
 * @param pingFn
 * @returns
 */
async function measureNetworkSpeed(
  pingFn: () => Promise<any>,
  headerSize: number
): Promise<number> {
  const startTime = Date.now();
  await pingFn();
  const duration = Date.now() - startTime;
  const speed = headerSize / (duration / 1000);
  return speed;
}
function calculateChunkSize(speed: number, perSeconds: number = 5) {
  const chunkSize = speed * perSeconds;
  return chunkSize;
}
function splitFileHeader(file: File, headerSize: number = 5120): [Blob, Blob] {
  const header = file.slice(0, headerSize);
  const body = file.slice(headerSize);
  return [header, body];
}
function splitFile(blob: Blob, chunkSize: number): Array<Blob> {
  const chunksCount = Math.ceil(blob.size / chunkSize);
  let chunkIndex = 0;
  const chunks: Array<Blob> = [];
  while (chunkIndex <= chunksCount) {
    const offset = chunkIndex * chunkSize;
    const chunk = blob.slice(offset, offset + chunkSize);
    if (chunk.size === 0) {
      break;
    }
    chunks[chunkIndex] = chunk;
    chunkIndex++;
  }
  return chunks;
}

function batchUpload(chunks: Array<Blob>, fileName: string) {
  return Promise.all(
    ([] as Array<Promise<any>>).concat(
      ..._.chunk(chunks, 5).map((subChunks, index) => {
        const indexOffset = index * 5;
        if (subChunks.length === 0) return Promise.resolve([]);
        return Promise.all(
          subChunks.map((chunk, index) =>
            uploadChunk(
              apiRoot + '/partial-upload',
              chunk,
              indexOffset + index,
              fileName
            )
          )
        );
      })
    )
  );
}

function sequence<T = any>(array: Array<() => Promise<T>>): Promise<T> {
  return array.reduce(
    (promiseChain, currentTask) => () => promiseChain().then(currentTask),
    () => Promise.resolve<T>(undefined as unknown as T)
  )();
}

async function incrementalUpload(
  uploadUrl,
  blob: Blob,
  fileName: string,
  updateProgress: (progress: number) => void,
  headers?: any
): Promise<any> {
  const oneMB = 1000000;
  var defaultUploadBytes: number = 2 * oneMB;
  var start: number = 0;
  var remainingBytes: number = blob.size;
  var index: number = 0;

  while(remainingBytes > 0) {
    var uploadBytes = defaultUploadBytes > remainingBytes ? remainingBytes : defaultUploadBytes;
    var end: number = start + uploadBytes;
    var partialBlob: Blob = blob.slice(start, end);
    const startTime = Date.now();
    await uploadChunk(uploadUrl, partialBlob, index, fileName, headers);
    const secondDuration: number = (Date.now() - startTime) / 1000;

    if (secondDuration < 2.5) {
      defaultUploadBytes += oneMB;
    }
    start = end;
    remainingBytes  = remainingBytes - uploadBytes;
    updateProgress(100 - Math.ceil((remainingBytes / blob.size) * 100));
  }
  return 1;
}

function sequenceUpload(
  uploadUrl,
  chunks: Array<Blob>,
  fileName: string,
  updateProgress: (progress: number) => void,
  headers?: any
): Promise<any> {
  return sequence(
    chunks.map((chunk, index) => async () => {
      await uploadChunk(uploadUrl, chunk, index, fileName, headers);
      updateProgress(Math.ceil((index / chunks.length) * 100));
      console.log('Uploaded chunk ' + index);
      console.log('Progress: ' + (index / chunks.length) * 100 + '%');
    })
  );
}

function mergeUploadedChunks(fileName: string) {
  fetch(apiRoot + '/merge', {
    method: 'POST',
    body: JSON.stringify({ fileName }),
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

function saveUploadedChunks(
  saveUrl: string,
  folderId: string,
  referenceId: number
) {
  return fetch(saveUrl, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      folderId,
      referenceId,
    }),
  }).then(response => response.json());
}

const uploadChunk = async (
  uploadUrl: string,
  file: Blob,
  index: number,
  fileName: string,
  headers?: any
) => {
  const formData = new FormData();
  formData.append('fileName', fileName);
  formData.append('index', index.toString());
  formData.append('data', file);
  const response = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
    mode: 'cors',
    headers,
  });
  const data = await response.json().catch(error => {
    return null;
  });
  return data;
};

async function uploadSplitFile(
  file: File,
  folderId: string,
  referenceId: number,
  updateProgress: (progress: number) => void
) {
  const token = getToken();
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const { uploadUrl, saveUrl } = await initUpload(file, folderId, headers);
  await incrementalUpload(uploadUrl, file, file.name, updateProgress);
  await saveUploadedChunks(saveUrl, folderId, referenceId);
  
  return { url: uploadUrl };
}

function listFile(): Promise<any> {
  return fetch(apiRoot + '/files', {
    method: 'GET',
  }).then(response => response.json());
}
